import { useState, useMemo } from "react";
import type React from "react";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth, hasPermission } from "@/lib/auth";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ClipboardList, RefreshCw, ChevronDown, ChevronRight, ChevronLeft,
} from "lucide-react";
import { format } from "date-fns";

// ─── types ─────────────────────────────────────────────────────────────────

/** Metadata row returned by the list endpoint (Changes excluded). */
interface AuditRow {
  id: string;
  timestamp: string;
  action: string;
  entityName: string;
  entityId: string | null;
  userId: string | null;
  organizationId: string | null;
  ipAddress: string | null;
}

/** Full record returned by GET /api/audit/{id}/details */
interface AuditDetailRow extends AuditRow {
  changes: string | null;
}

interface AuditPageResult {
  items: AuditRow[];
  page: number;
  pageSize: number;
  totalCount: number;
}

// ─── helpers ───────────────────────────────────────────────────────────────

const KNOWN_ACTIONS = [
  "AdminPasswordResetTriggered",
  "UserCreated",
  "UserUpdated",
  "UserDeleted",
  "Added",
  "Modified",
  "Deleted",
  "PasswordResetRequest",
  "PasswordResetSuccess",
  "PasswordResetFailed",
  "ImpersonationStart",
];

function actionBadgeVariant(action: string): "default" | "destructive" | "secondary" | "outline" {
  if (action.toLowerCase().includes("delete") || action === "Deleted") return "destructive";
  if (action.toLowerCase().includes("created") || action === "Added") return "default";
  if (action.toLowerCase().includes("password")) return "secondary";
  return "outline";
}

/** Sensitive-field names that must NEVER appear in the audit detail view. */
const SENSITIVE_KEY_RE = /password|hash|token|stamp|secret|api.?key|private|credential/i;

/**
 * Safely parse a Changes value for display.
 * Returns one of:
 *   { kind: "json",      obj: object }   — valid JSON object/array
 *   { kind: "string",    text: string }  — non-JSON or plain string
 *   { kind: "encrypted"               }  — enc:v1:… blob (server-side cipher)
 *   { kind: "empty"                   }  — null / blank
 */
function safeParseChanges(raw: string | null | undefined):
  | { kind: "json"; obj: Record<string, unknown> }
  | { kind: "string"; text: string }
  | { kind: "encrypted" }
  | { kind: "empty" } {
  if (!raw || !raw.trim()) return { kind: "empty" };
  if (raw.startsWith("enc:")) return { kind: "encrypted" };
  try {
    const parsed = JSON.parse(raw);
    if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
      const safe: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        safe[k] = SENSITIVE_KEY_RE.test(k) ? "[redacted]" : v;
      }
      return { kind: "json", obj: safe };
    }
    return { kind: "string", text: JSON.stringify(parsed, null, 2) };
  } catch {
    return { kind: "string", text: raw };
  }
}

function renderChangesDetail(raw: string | null | undefined): React.ReactNode {
  const parsed = safeParseChanges(raw);
  switch (parsed.kind) {
    case "empty":
      return <span className="text-muted-foreground italic">(no changes recorded)</span>;
    case "encrypted":
      return (
        <span className="text-muted-foreground italic">
          (changes are stored encrypted — contact a platform administrator if you need the full record)
        </span>
      );
    case "json":
      return (
        <pre className="bg-background border rounded p-2 overflow-x-auto text-[11px] leading-relaxed max-h-64 whitespace-pre-wrap break-all">
          {JSON.stringify(parsed.obj, null, 2)}
        </pre>
      );
    case "string":
      return (
        <pre className="bg-background border rounded p-2 overflow-x-auto text-[11px] leading-relaxed max-h-64 whitespace-pre-wrap break-all">
          {parsed.text}
        </pre>
      );
  }
}

function buildQs(params: Record<string, string | number>) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== "" && v !== 0) qs.set(k, String(v));
  }
  return qs.toString();
}

// ─── expanded-row detail panel ─────────────────────────────────────────────

function RowDetail({ row }: { row: AuditRow }) {
  const { data, isLoading } = useQuery<AuditDetailRow>({
    queryKey: ["audit-detail", row.id],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/audit/${row.id}/details`);
      if (!res.ok) throw new Error("Failed to load details");
      return res.json();
    },
    staleTime: 5 * 60 * 1000, // detail is immutable — cache 5 min
  });

  return (
    <TableRow className="bg-muted/20">
      <TableCell colSpan={8} className="py-3 px-5">
        <div className="text-xs space-y-2">
          {/* summary fields */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1 text-muted-foreground pb-1 border-b">
            <span>
              <span className="font-medium text-foreground">Timestamp:</span>{" "}
              {format(new Date(row.timestamp), "yyyy-MM-dd HH:mm:ss")}
            </span>
            <span><span className="font-medium text-foreground">Action:</span>{" "}{row.action}</span>
            <span><span className="font-medium text-foreground">Entity:</span>{" "}{row.entityName || "—"}</span>
            <span>
              <span className="font-medium text-foreground">Entity ID:</span>{" "}
              <span className="font-mono break-all">{row.entityId || "—"}</span>
            </span>
            <span>
              <span className="font-medium text-foreground">Actor ID:</span>{" "}
              <span className="font-mono break-all">{row.userId || "—"}</span>
            </span>
            <span><span className="font-medium text-foreground">IP Address:</span>{" "}{row.ipAddress || "—"}</span>
            <span>
              <span className="font-medium text-foreground">Event ID:</span>{" "}
              <span className="font-mono break-all">{row.id}</span>
            </span>
            <span>
              <span className="font-medium text-foreground">Org ID:</span>{" "}
              <span className="font-mono break-all">{row.organizationId || "—"}</span>
            </span>
          </div>

          {/* changes — lazy loaded */}
          <div>
            <p className="font-medium text-muted-foreground mb-1">
              Changes
              <span className="text-destructive text-[10px] ml-2 font-normal">
                (passwords, tokens and hashes are never stored here)
              </span>
            </p>
            {isLoading ? (
              <Skeleton className="h-16 w-full rounded" />
            ) : (
              renderChangesDetail(data?.changes ?? null)
            )}
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
}

// ─── skeleton rows ──────────────────────────────────────────────────────────

function SkeletonRows({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <TableRow key={i}>
          <TableCell className="pr-0"><Skeleton className="h-3 w-3 rounded" /></TableCell>
          <TableCell><Skeleton className="h-3 w-32 rounded" /></TableCell>
          <TableCell><Skeleton className="h-5 w-20 rounded-full" /></TableCell>
          <TableCell><Skeleton className="h-3 w-20 rounded" /></TableCell>
          <TableCell><Skeleton className="h-3 w-28 rounded" /></TableCell>
          <TableCell><Skeleton className="h-3 w-28 rounded" /></TableCell>
          <TableCell><Skeleton className="h-3 w-24 rounded" /></TableCell>
          <TableCell><Skeleton className="h-3 w-40 rounded" /></TableCell>
        </TableRow>
      ))}
    </>
  );
}

// ─── page ──────────────────────────────────────────────────────────────────

export default function AuditGeneralPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // ── pending filter state (user is typing) ─────────────────────────────────
  const [from, setFrom]     = useState("");
  const [to, setTo]         = useState("");
  const [action, setAction] = useState("all");
  const [entity, setEntity] = useState("");
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState("50");

  // ── applied filter state (committed on "Apply") ───────────────────────────
  const [applied, setApplied] = useState({
    from: "", to: "", action: "all", entity: "", search: "", pageSize: "50",
  });

  // ── pagination ────────────────────────────────────────────────────────────
  const [page, setPage] = useState(1);

  function apply() {
    setApplied({ from, to, action, entity, search, pageSize });
    setPage(1); // reset to first page on new filter
  }

  function clear() {
    setFrom(""); setTo(""); setAction("all"); setEntity(""); setSearch(""); setPageSize("50");
    setApplied({ from: "", to: "", action: "all", entity: "", search: "", pageSize: "50" });
    setPage(1);
  }

  const queryString = useMemo(() => buildQs({
    page,
    pageSize: applied.pageSize,
    from:     applied.from,
    to:       applied.to,
    action:   applied.action === "all" ? "" : applied.action,
    entity:   applied.entity,
    search:   applied.search,
  }), [applied, page]);

  const { data, isLoading, isFetching, dataUpdatedAt } = useQuery<AuditPageResult>({
    queryKey: ["audit-general", queryString],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/audit?${queryString}`);
      if (!res.ok) throw new Error("Failed to load audit log");
      return res.json();
    },
    // Keep previous page data visible while next page loads — no flash.
    placeholderData: keepPreviousData,
  });

  function handleRefresh() {
    queryClient.invalidateQueries({ queryKey: ["audit-general"] });
  }

  // ── expanded row ──────────────────────────────────────────────────────────
  const [expanded, setExpanded] = useState<string | null>(null);

  if (!hasPermission(user, "audit.view")) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        You do not have permission to view the audit log.
      </div>
    );
  }

  const rows      = data?.items ?? [];
  const total     = data?.totalCount ?? 0;
  const ps        = parseInt(applied.pageSize);
  const totalPages = Math.max(1, Math.ceil(total / ps));
  const rangeFrom  = total === 0 ? 0 : (page - 1) * ps + 1;
  const rangeTo    = Math.min(page * ps, total);

  return (
    <div className="p-6 space-y-4 max-w-7xl mx-auto">
      {/* ── header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-muted-foreground" />
            General Audit Log
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            User management, password changes, role assignments and other org events.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isFetching}
          data-testid="button-refresh-audit"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${isFetching ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* ── filters ── */}
      <div className="bg-muted/40 border rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* date from */}
          <div className="space-y-1">
            <label htmlFor="audit-filter-from" className="text-xs font-medium text-muted-foreground">From</label>
            <Input
              id="audit-filter-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && apply()}
              className="h-8 text-sm"
              data-testid="filter-from"
            />
          </div>
          {/* date to — backend uses exclusive < ToDate+1 so same-day From=To returns all records that day */}
          <div className="space-y-1">
            <label htmlFor="audit-filter-to" className="text-xs font-medium text-muted-foreground">To</label>
            <Input
              id="audit-filter-to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && apply()}
              className="h-8 text-sm"
              data-testid="filter-to"
            />
          </div>
          {/* action */}
          <div className="space-y-1">
            <span id="audit-filter-action-label" className="text-xs font-medium text-muted-foreground">Action</span>
            <Select value={action} onValueChange={(v) => setAction(v)}>
              <SelectTrigger className="h-8 text-sm" data-testid="filter-action" aria-labelledby="audit-filter-action-label">
                <SelectValue placeholder="All actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                {KNOWN_ACTIONS.map((a) => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* entity */}
          <div className="space-y-1">
            <label htmlFor="audit-filter-entity" className="text-xs font-medium text-muted-foreground">Entity</label>
            <Input
              id="audit-filter-entity"
              placeholder="e.g. AppUser"
              value={entity}
              onChange={(e) => setEntity(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && apply()}
              className="h-8 text-sm"
              data-testid="filter-entity"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          {/* search */}
          <div className="flex-1 min-w-[200px] space-y-1">
            <label htmlFor="audit-filter-search" className="text-xs font-medium text-muted-foreground">Search</label>
            <Input
              id="audit-filter-search"
              placeholder="Search action, entity, entity ID, IP…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && apply()}
              className="h-8 text-sm"
              data-testid="filter-search"
            />
          </div>
          {/* page size */}
          <div className="space-y-1">
            <span id="audit-filter-rows-label" className="text-xs font-medium text-muted-foreground">Rows</span>
            <Select value={pageSize} onValueChange={setPageSize}>
              <SelectTrigger className="h-8 text-sm w-20" data-testid="filter-page-size" aria-labelledby="audit-filter-rows-label">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["25", "50", "100"].map((n) => (
                  <SelectItem key={n} value={n}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" onClick={apply} data-testid="button-apply-filters">Apply</Button>
          <Button size="sm" variant="ghost" onClick={clear} data-testid="button-clear-filters">Clear</Button>
        </div>
      </div>

      {/* ── result meta + pagination ── */}
      <div className="flex items-center justify-between gap-4">
        <span className="text-xs text-muted-foreground">
          {isLoading
            ? "Loading…"
            : total === 0
              ? "No events found"
              : `Showing ${rangeFrom}–${rangeTo} of ${total.toLocaleString()} event${total !== 1 ? "s" : ""}`}
          {dataUpdatedAt && !isLoading
            ? ` · updated ${format(new Date(dataUpdatedAt), "HH:mm:ss")}`
            : ""}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || isLoading}
            data-testid="button-prev-page"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Prev
          </Button>
          <span className="text-xs text-muted-foreground px-2 min-w-[80px] text-center">
            {isLoading ? "…" : `Page ${page} of ${totalPages}`}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || isLoading}
            data-testid="button-next-page"
          >
            Next
            <ChevronRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* ── table ── */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30">
              <TableHead className="w-6"><span className="sr-only">Expand row</span></TableHead>
              <TableHead className="whitespace-nowrap">Timestamp</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Entity</TableHead>
              <TableHead>Entity ID</TableHead>
              <TableHead>Actor (User ID)</TableHead>
              <TableHead>IP Address</TableHead>
              <TableHead>Changes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <SkeletonRows count={parseInt(applied.pageSize)} />}
            {!isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-10 text-muted-foreground">
                  No events found for the selected filters.
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => {
              const isExpanded = expanded === row.id;
              return [
                <TableRow
                  key={row.id}
                  className="cursor-pointer hover:bg-muted/40"
                  onClick={() => setExpanded(isExpanded ? null : row.id)}
                  data-testid={`audit-row-${row.id}`}
                >
                  <TableCell className="pr-0">
                    {isExpanded
                      ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                      : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs font-mono">
                    {format(new Date(row.timestamp), "yyyy-MM-dd HH:mm:ss")}
                  </TableCell>
                  <TableCell>
                    <Badge variant={actionBadgeVariant(row.action)} className="text-xs font-mono">
                      {row.action}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">{row.entityName || "—"}</TableCell>
                  <TableCell className="text-xs font-mono truncate max-w-[140px]">
                    {row.entityId || "—"}
                  </TableCell>
                  <TableCell className="text-xs font-mono truncate max-w-[140px]">
                    {row.userId || "—"}
                  </TableCell>
                  <TableCell className="text-xs">{row.ipAddress || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[220px] truncate">
                    {/* Changes column is intentionally blank in the list view — expand to see */}
                    <span className="italic opacity-60">click to expand</span>
                  </TableCell>
                </TableRow>,
                isExpanded && <RowDetail key={`${row.id}-detail`} row={row} />,
              ];
            })}
          </TableBody>
        </Table>
      </div>

      {/* ── bottom pagination (repeat for long lists) ── */}
      {rows.length > 0 && (
        <div className="flex items-center justify-end gap-1 pt-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || isLoading}
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Prev
          </Button>
          <span className="text-xs text-muted-foreground px-2 min-w-[80px] text-center">
            {isLoading ? "…" : `Page ${page} of ${totalPages}`}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || isLoading}
          >
            Next
            <ChevronRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}
